import type { DiagramElement } from '@ukinfo/shared';
import { LAYER } from './PitchDiagram';

/**
 * Zeichenreihenfolge ("Nach vorne", "Nach hinten") - ohne React, damit sie sich einzeln pruefen lässt.
 *
 * Gezeichnet wird nach Ebene der Art (`LAYER`: Zone unten, dann Tor, Ring, Huerde, Huetchen und Stange, Pfeil, Masslinie, Spieler, Ball,
 * Text oben), innerhalb einer Ebene in der Reihenfolge der Liste: was spaeter steht, liegt weiter oben. "Nach vorne" und "Nach hinten"
 * vertauschen deshalb nur Elemente derselben Ebene (ein Spieler ueber den anderen) und lassen alle anderen Plaetze der Liste stehen.
 *
 * Pfeile bleiben, wo sie sind: ihre Reihenfolge in der Liste bestimmt die Schritte des Ablaufs ohne eingetragene Schrittnummer
 * (`diagramSteps`), Spieler und Ball im Ablauf folgen ihr bei gleichem Abstand.
 */

export type OrderDirection = 'front' | 'back';

/** Pfeile haben keine eigene Zeichenreihenfolge (siehe oben). */
export function isOrderable(element: DiagramElement | undefined): boolean {
  return element !== undefined && element.type !== 'arrow';
}

export type OrderResult =
  | {
      ok: true;
      elements: DiagramElement[];
      /** Neue Plaetze der markierten Elemente (aufsteigend). */
      selection: number[];
      /** Neuer Platz je alter Platz: fuer alles, was einen Index merkt (Bezugsobjekt). */
      moved: (index: number) => number;
    }
  | { ok: false; problem: string };

/**
 * Setzt die markierten Elemente innerhalb ihrer Ebene ganz nach vorn oder hinten, in ihrer bisherigen Reihenfolge. Je Ebene tauschen nur
 * die Plaetze dieser Ebene die Elemente; die Plaetze aller anderen Elemente (Pfeile!) bleiben unveraendert.
 */
export function reorderSelection(elements: readonly DiagramElement[], selection: readonly number[], direction: OrderDirection): OrderResult {
  const picked = new Set(selection.filter((index) => isOrderable(elements[index])));
  if (picked.size === 0) {
    return { ok: false, problem: 'Pfeile behalten ihre Reihenfolge – sie bestimmt die Schritte im Ablauf. Nach vorne oder hinten lassen sich die anderen Elemente setzen.' };
  }
  // Neue Anordnung: `order[platz]` ist der alte Platz des Elements, das dort jetzt steht.
  const order = elements.map((_, index) => index);
  const layers = new Set([...picked].map((index) => LAYER[elements[index]!.type]));
  for (const layer of layers) {
    const slots = elements.flatMap((element, index) => (LAYER[element.type] === layer && isOrderable(element) ? [index] : []));
    const chosen = slots.filter((index) => picked.has(index));
    const rest = slots.filter((index) => !picked.has(index));
    const arranged = direction === 'front' ? [...rest, ...chosen] : [...chosen, ...rest];
    slots.forEach((slot, position) => {
      order[slot] = arranged[position]!;
    });
  }
  if (order.every((old, index) => old === index)) {
    return {
      ok: false,
      problem: direction === 'front' ? 'Das Element liegt schon ganz vorn – vor Elementen gleicher Art.' : 'Das Element liegt schon ganz hinten – hinter Elementen gleicher Art.',
    };
  }
  const placeOf = new Map(order.map((old, index) => [old, index]));
  const moved = (index: number): number => placeOf.get(index) ?? index;
  return {
    ok: true,
    elements: order.map((old) => elements[old]!),
    selection: selection.map(moved).sort((a, b) => a - b),
    moved,
  };
}

/** Kann der Knopf etwas bewirken? Fuer den Zustand der Knoepfe in der Auswahlleiste. */
export function canReorder(elements: readonly DiagramElement[], selection: readonly number[], direction: OrderDirection): boolean {
  return reorderSelection(elements, selection, direction).ok;
}
